import {
  RenderCancelledError,
  RenderTimeoutError,
  UnsupportedMediaError,
  type CreateCanvas,
} from './types';
import { describeRecordedFormat, type VideoFormat } from './videoFormat';

type MediaRecorderConstructor = new (
  stream: MediaStream,
  options?: MediaRecorderOptions,
) => MediaRecorder;

export type EncodeFramesInput = {
  frames: readonly CanvasImageSource[];
  width: number;
  height: number;
  fps: number;
  format: VideoFormat;
  createCanvas: CreateCanvas;
  signal?: AbortSignal;
  videoBitsPerSecond?: number;
  MediaRecorderImpl?: MediaRecorderConstructor;
  now?: () => number;
};

type CaptureTrack = MediaStreamTrack & { requestFrame?: () => void };

/**
 * Plays pre-rendered frames into a canvas stream in real time and records it. Frames
 * are rendered before recording starts, so slow rasterization cannot stretch the clip.
 */
export function encodeFramesWithMediaRecorder(
  input: EncodeFramesInput,
): Promise<{ blob: Blob; format: VideoFormat }> {
  const Recorder =
    input.MediaRecorderImpl ??
    (globalThis as { MediaRecorder?: MediaRecorderConstructor }).MediaRecorder;
  if (!Recorder) {
    return Promise.reject(new UnsupportedMediaError('MediaRecorder is not available.'));
  }
  if (input.frames.length === 0) {
    return Promise.reject(new Error('A review video needs at least one frame.'));
  }
  const canvas = input.createCanvas(input.width, input.height);
  const context = canvas.getContext('2d');
  if (!context || typeof canvas.captureStream !== 'function') {
    return Promise.reject(new UnsupportedMediaError('Canvas capture is not available.'));
  }
  const now = input.now ?? (() => performance.now());
  const draw = (index: number) => {
    context.clearRect(0, 0, input.width, input.height);
    context.drawImage(input.frames[index], 0, 0, input.width, input.height);
  };

  draw(0);
  let stream = canvas.captureStream(0);
  let track = stream.getVideoTracks()[0] as CaptureTrack | undefined;
  if (typeof track?.requestFrame !== 'function') {
    stream.getTracks().forEach((candidate) => candidate.stop());
    stream = canvas.captureStream(input.fps);
    track = undefined;
  }

  let recorder: MediaRecorder;
  try {
    recorder = new Recorder(stream, {
      mimeType: input.format.mimeType,
      videoBitsPerSecond: input.videoBitsPerSecond ?? 4_000_000,
    });
  } catch (error) {
    stream.getTracks().forEach((candidate) => candidate.stop());
    return Promise.reject(
      new UnsupportedMediaError(
        `${input.format.mimeType} recording is not supported: ${
          error instanceof Error ? error.message : String(error)
        }`,
      ),
    );
  }

  return new Promise((resolve, reject) => {
    const chunks: Blob[] = [];
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    function onAbort() {
      const reason = input.signal?.reason;
      fail(reason instanceof RenderTimeoutError ? reason : new RenderCancelledError());
    }

    const cleanup = () => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      input.signal?.removeEventListener('abort', onAbort);
      stream.getTracks().forEach((candidate) => candidate.stop());
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
    };

    const fail = (error: Error) => {
      if (settled) {
        return;
      }
      settled = true;
      if (recorder.state !== 'inactive') {
        try {
          recorder.stop();
        } catch {
          // Already stopping.
        }
      }
      chunks.length = 0;
      cleanup();
      reject(error);
    };

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunks.push(event.data);
      }
    };
    recorder.onerror = () => fail(new Error('Video recording failed.'));
    recorder.onstop = () => {
      if (settled) {
        return;
      }
      settled = true;
      const format = describeRecordedFormat(input.format, recorder.mimeType);
      const blob = new Blob(chunks, { type: format.mimeType });
      cleanup();
      resolve({ blob, format });
    };

    if (input.signal?.aborted) {
      onAbort();
      return;
    }
    input.signal?.addEventListener('abort', onAbort, { once: true });

    try {
      recorder.start();
    } catch (error) {
      fail(
        new UnsupportedMediaError(
          `Recording could not start: ${error instanceof Error ? error.message : String(error)}`,
        ),
      );
      return;
    }

    const frameMs = 1000 / input.fps;
    const startedAt = now();
    let index = 0;
    const tick = () => {
      if (settled) {
        return;
      }
      if (index >= input.frames.length) {
        recorder.stop();
        return;
      }
      draw(index);
      track?.requestFrame?.();
      index += 1;
      timer = setTimeout(tick, Math.max(0, startedAt + index * frameMs - now()));
    };
    tick();
  });
}
